import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { setCachedResumesCount } from 'composables/useResumesCountCache.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// What a card in the list needs: the resume's details, its content and photo (so the card can draw the resume itself), and the run that
// wrote it when AI did.
const summarize = (resume) => ({
  id: resume.id,
  name: resume.name,
  template: resume.template,
  is_default: resume.is_default,
  headline: resume.headline ?? resume.content?.basics?.headline ?? null,
  ai_generated: Boolean(resume.ai_generated), // written by AI for a job
  ai_match_score: resume.ai_match_score ?? null, // how well it matches that job (0 to 100), as the AI scored it
  generated_for: resume.generated_for ?? null, // what it was written for, e.g. "Senior Engineer at Acme"
  job: resume.job ?? null, // the job, while it still exists: { id, title, company_name, company_logo_url }
  content: resume.content ?? null,
  picture_url: resume.picture_url ?? null,
  generation: resume.generation ?? null, // { model, input_tokens, cached_input_tokens, output_tokens, total_tokens, old_match_score, new_match_score, created_at, ... }
  created_at: resume.created_at,
  updated_at: resume.updated_at,
})

const newestFirst = (list) => [...list].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))

// The user's resumes in the CV builder. Every action returns { ok, message, resume } so the page
// can show the server's reason next to what the user was doing.
export const useResumesStore = defineStore('resumes', {
  state: () => ({
    resumes: [],
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) => state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
    defaultResume: (state) => state.resumes.find((resume) => resume.is_default) || null,
  },

  actions: {
    // Something changed elsewhere (the editor saved): the next visit refetches.
    invalidate() {
      this.fetchedAt = null
    },

    async fetchIfStale() {
      if (this.isCacheValid) return
      await this.fetchResumes()
    },

    // `silent` refreshes in the background: what is on screen stays, with no loading state.
    async fetchResumes({ silent = false } = {}) {
      if (!silent) this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/resumes', {}, false)
        if (!res.js_error && res.response.success) {
          this.resumes = res.response.data.records.map(summarize)
          this.fetchedAt = Date.now()
          setCachedResumesCount(this.resumes.length)
        } else {
          this.error = res.response?.message || 'Failed to load your resumes.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    create({ name, template }) {
      return this.send('POST', '/api/v1/resumes', { name, template }, 'Could not create the resume.')
    },

    // Sends only what is given, so a rename does not carry the document.
    update(id, attrs) {
      return this.send('PATCH', `/api/v1/resumes/${id}`, attrs, 'Could not save the resume.')
    },

    duplicate(id, name) {
      return this.send('POST', `/api/v1/resumes/${id}/duplicate`, name ? { name } : {}, 'Could not copy the resume.')
    },

    // The old default stops being one, here as on the server.
    async makeDefault(id) {
      const result = await this.send('POST', `/api/v1/resumes/${id}/make_default`, {}, 'Could not change the default.')
      if (result.ok) this.resumes.forEach((resume) => { resume.is_default = resume.id === id })
      return result
    },

    async remove(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/resumes/${id}`, {}, false)
      if (res.js_error || !res.response?.success) {
        return { ok: false, message: res.response?.message || 'Could not delete the resume.' }
      }

      const wasDefault = this.resumes.find((resume) => resume.id === id)?.is_default
      this.resumes = this.resumes.filter((resume) => resume.id !== id)
      setCachedResumesCount(this.resumes.length)
      // The server hands the default to another resume; ask which instead of guessing.
      if (wasDefault && this.resumes.length > 0) await this.fetchResumes({ silent: true })
      return { ok: true }
    },

    // One request, then the list is brought in line with the resume that came back.
    async send(method, path, body, fallback) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(method, path, body, false)
      if (res.js_error || !res.response?.success) {
        return { ok: false, message: res.response?.message || fallback }
      }

      const resume = summarize(res.response.data)
      const rest = this.resumes.filter((existing) => existing.id !== resume.id)
      this.resumes = newestFirst([...rest, resume])
      this.fetchedAt = Date.now()
      setCachedResumesCount(this.resumes.length)
      return { ok: true, resume }
    },
  },
})
